import { getTempInfoCountryConfig } from '../constants/countries';
import { LDFlags } from '../constants/flags';
import { isTreatedAsOther } from '../constants/lienholders';
import { Maybe, TemporaryData } from '../gql/generated/graphql';
import { usePayoffIntelligence } from './useAutoFetchPayoff/usePayoffIntelligence';
import { useLazyFlag } from './useLazyFlag';

type RoutingDecisionInput = {
  wantsToSell: boolean;
  tempInfoData: Maybe<Pick<TemporaryData, 'lienholder' | 'lienholder_slug' | 'country'>>;
};

type PostLienholderRouting = {
  skipPayoffAccordion: boolean;
  forceShowPayoffPage: boolean;
};

/**
 * Decides whether the standalone payoff page is forced or skipped after the
 * keep/sell + lienholder decision. Resolves payoff-intelligence support
 * internally, since that lookup exists only to feed this decision.
 *
 * Under HIDE_MARKETPLACE_LIST_PRICE, missing-field collection moves to the
 * marketplace dashboard, so the payoff page is not forced for that case. An
 * "Other" lienholder still forces it (payoff intelligence is unsupported).
 */
export const usePostLienholderRouting = () => {
  const evaluate = useLazyFlag();
  const { checkLienholderSupportedForPI } = usePayoffIntelligence();

  return async ({
    wantsToSell,
    tempInfoData,
  }: RoutingDecisionInput): Promise<PostLienholderRouting> => {
    if (!getTempInfoCountryConfig(tempInfoData).payoffCollection) {
      return { skipPayoffAccordion: true, forceShowPayoffPage: false };
    }

    const isOtherLienholder = isTreatedAsOther(tempInfoData);

    const { isSupported, missingRequiredFields } = await checkLienholderSupportedForPI();

    const hideListPrice = evaluate(LDFlags.HIDE_MARKETPLACE_LIST_PRICE);
    const comHidePayoffPage = evaluate(LDFlags.COM_HIDE_PAYOFF_PAGE);

    const shouldShowPayoffPageForMissingFields =
      !hideListPrice && wantsToSell && isSupported && missingRequiredFields.length > 0;
    const forceShowPayoffPage = shouldShowPayoffPageForMissingFields || isOtherLienholder;
    const skipPayoffAccordion = (comHidePayoffPage || isSupported) && !forceShowPayoffPage;

    return { skipPayoffAccordion, forceShowPayoffPage };
  };
};
